import { AuthService } from '../auth/auth.service';
import { LanguageService } from '../shared/services/language.service';
import { ModelMapService } from './model-map.service';

import { DatexPipe } from '../shared/datex.pipe';

import { BreakpointObserver } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  model,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import {
  ChangeContext,
  LabelType,
  NgxSliderModule,
  Options,
} from '@angular-slider/ngx-slider';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { LngLatLike } from 'mapbox-gl';
import { Moment } from 'moment';
import moment from 'moment/moment';
import { Subscription, timer } from 'rxjs';
import { sliderBaseOptions } from 'src/constants';
import { isNil } from 'src/utils/utils';

export const MODULES = [
  CommonModule,
  ReactiveFormsModule,
  MatFormFieldModule,
  MatSelectModule,
  TranslateModule,
  MatCardModule,
  MatIconModule,
  MatButtonModule,
  MatDividerModule,
  NgxSliderModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [DatexPipe];

@Component({
  selector: 'app-model',
  templateUrl: './model.component.html',
  styleUrls: ['./model.component.scss', './model-legende.scss'],
  providers: [ModelMapService],
  imports: [...MODULES, ...COMPONENTS, ...PIPES],
})
export class ModelComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private platform = inject(Platform);
  private breakpointObserver = inject(BreakpointObserver);
  private auth = inject(AuthService);
  public modelMapService = inject(ModelMapService);
  public languageService = inject(LanguageService);

  models = signal([]);
  modelDetails = signal({});
  runTimes = signal<Array<Moment>>([]);
  runData = {};
  regions = signal([]);
  mapCenter: LngLatLike;
  mapZoomLevel = 3;
  animationIsRunning = signal(false);
  sliderDate = model<number>();
  sliderDates: number[];
  sliderOptions = signal<Options>(sliderBaseOptions);
  animationSubscription: Subscription;
  animationSpeed = 500;

  modelForm = this.fb.group({
    model: new FormControl<string | null>(null),
    region: new FormControl<string>('eu'),
    runValue: new FormControl<string | null>(null),
    parameter: new FormControl<string | null>(null),
  });

  get isMobilePlatform() {
    return (
      this.breakpointObserver.isMatched(['(max-width: 950px)']) ||
      this.platform.ANDROID ||
      this.platform.IOS
    );
  }

  selectedModel = toSignal(this.modelForm.controls.model.valueChanges, {
    initialValue: this.modelForm.controls.model.value,
  });

  selectedRegion = toSignal(this.modelForm.controls.region.valueChanges, {
    initialValue: this.modelForm.controls.region.value,
  });

  selectedRunValue = toSignal(this.modelForm.controls.runValue.valueChanges, {
    initialValue: this.modelForm.controls.runValue.value,
  });

  selectedParameter = toSignal(this.modelForm.controls.parameter.valueChanges, {
    initialValue: this.modelForm.controls.parameter.value,
  });

  get mapDtg(): Moment {
    return moment.unix(this.sliderDate());
  }
  ngOnInit(): void {
    this.getProductDetails();
    this.modelMapService.language = this.auth.getLanguage();
    this.initModelData(true);
  }

  ngOnDestroy(): void {
    if (this.animationSubscription) {
      this.animationSubscription.unsubscribe();
    }
  }

  initModelData(firstTime: boolean): void {
    this.modelMapService
      .getModelData(
        this.selectedModel(),
        this.selectedParameter(),
        this.selectedRegion(),
        isNil(this.selectedRunValue())
          ? undefined
          : moment(this.selectedRunValue())
      )
      .subscribe(data => {
        this.runData = data.runData;
        this.runTimes.set([...data.runTimes]);

        if (isNil(this.selectedRunValue())) {
          this.modelForm.patchValue({
            runValue: this.runTimes()[0]?.clone().toISOString(),
          });
        }

        const firstTimeStep = moment(
          this.runData[this.selectedRunValue()][0].dtg
        );
        if (firstTime) {
          this.modelMapService.buildMap(
            this.mapCenter,
            this.selectedModel(),
            this.selectedParameter(),
            this.selectedRegion(),
            moment(this.selectedRunValue()),
            firstTimeStep
          );
        } else {
          this.modelMapService.removeModelLayers();
          this.modelMapService.addModelLayers(
            this.selectedModel(),
            this.selectedParameter(),
            this.selectedRegion(),
            moment(this.selectedRunValue()),
            firstTimeStep
          );
        }

        this.setSliderOptions();
      });
  }

  setSliderOptions(): void {
    const data = this.runData[this.selectedRunValue()];
    const tickStep = data.length > 40 ? 8 : data.length > 20 ? 4 : 2; //Math.round(data.length / 10);
    this.sliderDates = [];
    this.sliderOptions.set({
      showTicks: true,
      showTicksValues: true,
      stepsArray: data.map((feature: any) => {
        const dtg = feature.dtg;
        let legend = moment(dtg).format('dd HH:mm');
        if (this.isMobilePlatform) {
          legend = moment(dtg).format('dd H');
        }

        const dtgUnix = moment(dtg).unix();
        this.sliderDates.push(dtgUnix);
        return {
          value: dtgUnix,
          legend,
        };
      }),
      ticksValuesTooltip: (step: number) => {
        const dtg = moment(step);
        return dtg.format('HH:mm');
      },
      tickStep,
      getTickColor: (value: number): string => {
        return '#399DE6';
      },
      translate: (value: number, label: LabelType): string => {
        return null;
      },
    });
    this.setSliderDate(0);
  }

  getProductDetails(): void {
    const customerDocument = this.auth.getCustomerDocument();
    const models = [];
    const regions = [];
    const modelDetails = {};
    let center: LngLatLike;
    if (
      customerDocument.hasOwnProperty('modelProducts') &&
      customerDocument.modelProducts.length > 0
    ) {
      for (const modelProduct of customerDocument.modelProducts) {
        if (this.auth.isProductActive('modelProducts', modelProduct.id)) {
          if (
            center === undefined &&
            modelProduct.hasOwnProperty('points') &&
            modelProduct.points !== null &&
            modelProduct.points.length > 0
          ) {
            center = [
              modelProduct.points[0].longitude,
              modelProduct.points[0].latitude,
            ];
            this.mapZoomLevel = modelProduct.points[0].zoom;
          }
          if (
            modelProduct.hasOwnProperty('regions') &&
            modelProduct.regions !== null
          ) {
            for (const region of modelProduct.regions) {
              if (regions.indexOf(region) < 0) {
                regions.push(region);
              }
            }
          }
          if (
            modelProduct.hasOwnProperty('modelDetails') &&
            modelProduct.modelDetails !== null &&
            modelProduct.modelDetails.length > 0
          ) {
            for (const detail of modelProduct.modelDetails) {
              if (models.indexOf(detail.model) < 0) {
                models.push(detail.model);
              }
              if (!modelDetails.hasOwnProperty(detail.model)) {
                modelDetails[detail.model] = detail.parameters;
              } else {
                for (const parameter of detail.parameters) {
                  if (modelDetails[detail.model].indexOf(parameter) < 0) {
                    modelDetails[detail.model].push(parameter);
                  }
                }
              }
            }
          }
        }
      }
    }
    this.models.set([...models]);
    this.modelDetails.set({ ...modelDetails });
    if (models.length > 0) {
      this.selectDefaultModel();
      this.selectDefaultParameter();
    }
    this.mapCenter = center;
    this.regions.set(regions.length > 0 ? [...regions] : ['eu']);
  }

  setSliderDate(pos: number): void {
    this.sliderDate.set(this.sliderDates[pos]);
    this.modelMapService.setTimeStep(pos);
  }

  startTimer(): void {
    const marwisTimer = timer(0, this.animationSpeed);
    this.animationSubscription = marwisTimer.subscribe(() => {
      const oldSliderPos = this.sliderDates.indexOf(this.sliderDate());
      const newSliderPos =
        oldSliderPos >= this.sliderDates.length - 1 ? 0 : oldSliderPos + 1;
      this.sliderDate.set(this.sliderDates[newSliderPos]);
      this.modelMapService.setTimeStep(newSliderPos);
    });
  }

  stopTimer(): void {
    if (this.animationSubscription) {
      this.animationSubscription.unsubscribe();
    }
  }

  onChangeModel(selection: MatSelectChange): void {
    this.modelForm.patchValue({ runValue: null });
    this.selectDefaultParameter();
    this.initModelData(false);
  }

  onChangeRun(selection: MatSelectChange): void {
    this.initModelData(false);
  }

  onChangeParameter(selection: MatSelectChange): void {
    this.initModelData(false);
  }

  onChangeRegion(selection: MatSelectChange): void {
    this.initModelData(false);
    this.modelMapService.flyToRegion(this.selectedRegion());
  }

  onChangeSpeed(selection: MatSelectChange): void {
    this.animationSpeed = selection.value;
    if (this.animationIsRunning()) {
      this.stopTimer();
      this.startTimer();
    }
  }

  onPlay(): void {
    this.animationIsRunning.set(true);
    this.startTimer();
  }

  onPause(): void {
    this.animationIsRunning.set(false);
    this.stopTimer();
  }

  onSliderChange(event: ChangeContext): void {
    if (this.animationIsRunning()) {
      this.animationIsRunning.set(false);
      this.stopTimer();
    }
    const pos = this.sliderDates.indexOf(event.value);
    this.setSliderDate(pos);
  }

  private selectDefaultModel() {
    this.modelForm.patchValue({
      model: this.models()[0],
    });
  }

  private selectDefaultParameter() {
    this.modelForm.patchValue({
      parameter: this.modelDetails()[this.selectedModel()][0],
    });
  }
}
